<template>
  <div>
    <h3 class="title is-3">Raw activities</h3>
    <button class="button is-primary" @click="rawActivityCreate">Create an activity</button>
    <button class="button is-primary btn-margin" @click="rawActivitiesImport">Import activities</button>

    <p>&nbsp;</p>

    <div class="block">
      <b-field grouped>
        <b-input
            v-model="searchFilter"
            icon="magnify"
            expanded
            placeholder="Search for activities..."
            @input="searchFilterChanged">
        </b-input>
        <b-checkbox-button
            v-model="unlockModeFilter"
            :native-value="checkedUnlocked"
            style="padding-right: 4px"
            type="is-success"
            @input="geolocFilterChanged(checkedUnlocked)"
        >
          <b-icon :icon="iconUnlockFilter"></b-icon>
          <span>Unlocked activities</span>
        </b-checkbox-button>
        <b-checkbox-button
            v-model="unlockModeFilter"
            :native-value="checkedLocked"
            style="padding-right: 4px"
            type="is-danger"
            @input="geolocFilterChanged(checkedLocked)"
        >
          <b-icon :icon="iconLockedFilter"></b-icon>
          <span>Locked activities</span>
        </b-checkbox-button>
      </b-field>
      <b-field grouped position="is-right">
        <b-checkbox-button v-for="(typAct, index) in activityTypesArray"
                           :key="index"
                           v-model="activityTypeFilter"
                           :expanded="false"
                           :native-value="typAct"
                           style="padding-right: 4px"
                           type="is-info"
                           @input="searchFilterChanged"
        >
          <b-icon icon="check"></b-icon>
          <span>{{ typAct }}</span>
        </b-checkbox-button>
      </b-field>
    </div>

    <b-table
        :data="rawActivitiesFiltered"
        :default-sort="['name', 'desc']"
        :hoverable="true"
        :loading="isLoading"
        :paginated="true"
        :pagination-simple="true"
        :per-page="50"
        :striped="true">
      <template slot-scope="props">
        <b-table-column class="centered-table-content" field="imgSrc" label="Picture">
          <b-img :height="90" :img="props.row.imgSrc" :width="120"></b-img>
        </b-table-column>

        <b-table-column class="centered-table-content" field="name" label="Name" sortable>
          {{ props.row.name }}
          <b-tooltip :label="'Timer: ' + props.row.timeToAnswer + ' seconds'">
            <b-icon v-if="isThereATimer(props.row)" icon="clock-fast" type="is-danger"></b-icon>
          </b-tooltip>
        </b-table-column>

        <b-table-column class="centered-table-content" field="activityType" label="activityType">
          <b-tag type="is-success">{{ props.row.activityType }}</b-tag>
        </b-table-column>

        <b-table-column
            class="centered-table-content"
            field="content"
            label="Content"
            width="400px"
            v-html="displayContent(props.row)">
        </b-table-column>

        <b-table-column class="centered-table-content" field="points" label="Points">
          {{ props.row.points }}
        </b-table-column>

        <b-table-column class="centered-table-content" field="difficulty" label="Difficulty">
          {{ props.row.difficulty }}
        </b-table-column>

        <b-table-column class="centered-table-content" field="tags" label="Tags">
          <b-taglist>
            <b-tag
                v-for="(tag, index) in props.row.tags"
                :key="index"
                type="is-info">{{ tag }}
            </b-tag>
          </b-taglist>
        </b-table-column>

        <b-table-column class="centered-table-content" label="Actions">
          <b-button icon-right="pen" size="large" type="is-text"
                    @click="rawActivityEdit(props.row.rawActivityId)"></b-button>
          <b-button icon-right="delete" size="large"
                    type="is-text" @click="openRawActivityDeleteDialog(props.row.rawActivityId)"></b-button>
        </b-table-column>
      </template>
    </b-table>

  </div>
</template>

<script>
import rawActivityService from '../services/rawActivityService'
import displayHelper from '../helpers/display-helper'
import {DialogProgrammatic as Dialog} from 'buefy'
import BImg from './single/BImg'
import rawActivitiesHelper from '../helpers/rawActivities-helper'
import activityTypesHelper from '@/enums/activityTypes'
import constantHelper from '@/enums/constants'
import _ from 'lodash'

const CHECKED_LOCKED = 'locked'
const CHECKED_UNLOCKED = 'unlocked'

export default {
  name: 'RawActivities',
  components: {
    BImg,
  },
  data() {
    return {
      isLoading: false,
      rawActivities: [],
      activityTypeFilter: [],
      unlockModeFilter: [],
      activityTypesArray: [],
      checkedLocked: CHECKED_LOCKED,
      checkedUnlocked: CHECKED_UNLOCKED,
      iconLockedFilter: 'check',
      iconUnlockFilter: 'check',
      defaultImgSrc: '',
      rawActivitiesFiltered: [],
      searchFilter: null,
      columns: [
        {field: 'name'},
        {field: 'question'},
        {field: 'tags'},
      ],
    }
  },
  created() {
    this.isLoading = true
    this.defaultImgSrc = constantHelper.GENERIC_PHOTO_URL

    this.unlockModeFilter = [CHECKED_LOCKED, CHECKED_UNLOCKED]

    this.activityTypesArray = _.filter(Object.values(activityTypesHelper.ACTIVITY_TYPES), (type) => {
      return !activityTypesHelper.disabledActivities(type)
    }).sort()
    this.activityTypeFilter = this.activityTypesArray

    this.loadRawActivitiesList()
  },
  methods: {
    loadRawActivitiesList() {
      var parent = this
      rawActivityService.getRawActivities()
          .then(rawActivities => {
            this.rawActivities = rawActivities
            this.rawActivitiesFiltered = rawActivities
            this.isLoading = false
            this.searchFilterChanged()
          })
          .catch((err) => {
            displayHelper.displayError(parent, 'Oops.. An error occurred: ' + err.message)
          })
    },
    rawActivityCreate() {
      this.$router.push({name: 'CreateRawActivity'})
    },
    openRawActivityDeleteDialog(rawActivityId) {
      Dialog.confirm({
        message: 'Are you SURE you want to delete this activity?',
        onConfirm: () => this.deleteRawActivity(rawActivityId)
      })
    },
    rawActivityEdit(rawActivityId) {
      this.$router.push({name: 'EditRawActivity', params: {id: rawActivityId}})
    },
    rawActivitiesImport() {
      this.$router.push({name: 'RawActivitiesImport'})
    },
    deleteRawActivity(rawActivityId) {
      var parent = this
      this.isLoading = true
      rawActivityService.deleteRawActivity(rawActivityId)
          .then(() => {
            displayHelper.displaySuccess(parent, 'Activity successfully deleted')
          })
          .catch((err) => {
            displayHelper.displayError(parent, 'Error while deleting the activity: ' + err.message)
          })
          .finally(() => {
            this.loadRawActivitiesList()
          })
    },
    getImageBackground(img) {
      return 'background-image:url(' + img ? img : this.defaultImgSrc + ');'
    },
    displayContent(rawActivity) {
      return rawActivitiesHelper.displayContentForActivityType(rawActivity)
    },
    searchFilterChanged() {
      if (this.searchFilter && this.searchFilter !== '') {
        let searchLower = this.searchFilter.toLowerCase()
        this.rawActivitiesFiltered = _.filter(this.rawActivities, r => {
          const rowContent = r.content
          let found = false
          for (let col of this.columns) {
            const theRow = col.field === 'question' ? rowContent.question : r[col.field]
            if (theRow) {
              found = theRow.toString().toLowerCase().indexOf(searchLower) >= 0 &&
                  this.includeGeolocInFilter(rowContent.unlocked_forced) &&
                  this.includeActivityTypeFilter(r['activityType'])
              if (found) {
                return true
              }
            }
          }
          return found
        })
      } else {
        this.rawActivitiesFiltered = _.filter(this.rawActivities, r => {
          return this.includeGeolocInFilter(r['content'].unlocked_forced) &&
              this.includeActivityTypeFilter(r['activityType'])
        })
      }
    },
    includeGeolocInFilter(lock) {
      if (lock && this.unlockModeFilter.includes(this.convLockMode(lock))) {
        return true
      } else if (!lock && this.unlockModeFilter.includes(this.convLockMode(lock))) {
        return true
      }
      return false
    },
    includeActivityTypeFilter(activityType) {
      return this.activityTypeFilter.includes(activityType)
    },
    geolocFilterChanged(lock) {
      if (this.unlockModeFilter.includes(lock)) {
        if (lock === CHECKED_UNLOCKED) {
          this.iconUnlockFilter = 'check'
        }
        if (lock === CHECKED_LOCKED) {
          this.iconLockedFilter = 'check'
        }
      } else {
        if (lock === CHECKED_UNLOCKED) {
          this.iconUnlockFilter = 'close'
        }
        if (lock === CHECKED_LOCKED) {
          this.iconLockedFilter = 'close'
        }
      }
      this.searchFilterChanged()
    },
    convLockMode(lockMode) {
      return lockMode === true ? CHECKED_UNLOCKED : CHECKED_LOCKED
    },
    isThereATimer(activity) {
      return !!activity.timeToAnswer && activity.timeToAnswer > 0
    },
  }
}
</script>

<style scoped>
.btn-margin {
  margin-left: 10px;
  margin-right: 10px;
}
</style>
